<template>
  <div class="pb-5">
    <Notification v-if="isTrade" :text="$t('message.trade.tradeResultsNotif')"> </Notification>
    <div class="d-flex justify-content-between flex-column flex-sm-row" v-if="hasTradeInValuation">
      <AverageRange class="box-top mr-sm-2 ml-sm-2 mb-3" :low-price="lowPrice" :high-price="highPrice">
        <template v-slot:title>{{ averageRangeTitle }}</template>
      </AverageRange>
      <div class="vertical d-none d-sm-block"></div>
      <EstimatedValue
        v-if="isTrade"
        class="box-top ml-0 ml-sm-2 mr-sm-2"
        data-test="estimated-value"
        :price="averagePrice"
        :taxSavings="averageTaxSavingValue"
      >
      </EstimatedValue>
      <LowToHighRange
        v-if="isAcquire"
        class="box-top ml-0 ml-sm-2 mr-sm-2"
        data-test="acquire-range"
        :range="acquireEstimatedCashValueRange"
      >
        <template v-slot:title>{{ $t('message.acquire.estimatedCashValue.title') }}</template>
        <template v-slot:subtitle>{{ $t('message.acquire.estimatedCashValue.subTitle') }}</template>
      </LowToHighRange>
    </div>
    <div class="no-report-to-display" v-if="!hasTradeInValuation">
      <mobialis-card class="thank-you">
        <h4>{{ $t('message.trade.dealerConfirmsTrade.title') }}</h4>
        <p v-if="isAcquire">{{ $t('message.trade.dealerConfirmsTrade.acquireSubtitle') }}</p>
        <p v-else>{{ $t('message.trade.dealerConfirmsTrade.subtitle') }}</p>
      </mobialis-card>
      <mobialis-card class="dealer-info">
        <div>
          <inline-svg :src="require('@/assets/ic-dealership.svg')" />
        </div>
        <div>
          <h5>{{ $store.getters['business/getBusinessName'] }}</h5>
          <p class="mt-0 w-50">{{ $store.getters['business/getBusinessFullAddress'] }}</p>
        </div>
      </mobialis-card>
    </div>
    <div v-if="isTrade && hasTradeInValuation">
      <ChevronDownButton
        v-if="subscribesToCredit && tradeInSourceIsTradalgo"
        class="my-5"
        data-test="next-steps-prompt"
        @click="scrollTo('topCard')"
      >
        <!--topCard is referencing an ID inside NextStep-->
        {{ $t('message.trade.nextStepFinancing') }}
      </ChevronDownButton>
      <TotalValue
        v-if="tradeInSourceIsTradalgo"
        :low-list-price="lowListPrice"
        :high-list-price="highListPrice"
        :low-price="lowPrice"
        :high-price="highPrice"
        class="my-3"
      ></TotalValue>
    </div>
    <CarfaxLogoContainer v-if="hasTradeInValuation && tradeInSourceIsCarfax" class="mb-2 mt-5" />
    <div v-if="isAcquire" class="mt-4 pt-4">
      <mobialis-card class="purchase-card py-sm-3" data-test="acquire-purchase-card">
        <h3>{{ $t('message.acquire.vehiclePurchaseCard.title') }}</h3>
        <h4 class="font-weight-normal">{{ $t('message.acquire.vehiclePurchaseCard.text') }}</h4>
      </mobialis-card>
    </div>
    <div v-if="displayCreditCTA" id="topCard" data-test="next-steps" class="pt-4">
      <h2 class="pb-2">{{ $t('message.trade.nextSteps.title') }}</h2>
      <NextStep></NextStep>
    </div>
    <mobialis-button
      v-if="!subscribesToCredit && !isKijijiDealer"
      @click="$router.push({ name: `home-${baseRoute}` })"
      class="mt-4 font-weight-bold"
      data-cy="continueResearchButton"
      v-av-analytics-cta-interaction="{
        elementText: $t('message.trade.applicationSubmittedButton'),
        eventActionResult: 'open',
      }"
    >
      {{ $t('message.trade.applicationSubmittedButton') }}
    </mobialis-button>
    <template v-if="disclaimer">
      <p class="disclaimer mt-5" v-html-safe="disclaimer" data-test="disclaimer"></p>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AverageRange from '@/components/trade-in/AverageRange.vue';
import LowToHighRange from '@/components/trade-in/LowToHighRange.vue';
import EstimatedValue from '@/components/trade-in/EstimatedValue.vue';
import TotalValue from '@/components/trade-in/TotalValue.vue';
import NextStep from '@/components/trade-in/NextStep.vue';
import ChevronDownButton from '@/components/ChevronDownButton.vue';
import { inDollars } from '@/utils/numbers';
import { AVRProducts } from '@/utils/enums';
import log from '@/services/log';
import Notification from '@/components/shared/Notification.vue';
import helperFunctions from '@/utils/helperFunctions';
import CarfaxLogoContainer from '@/components/shared/trade-in-carfax/CarfaxLogoContainer.vue';
import TradeInSource from '@/utils/enums/tradeInSource';

export default {
  name: 'MobileReport',
  components: {
    CarfaxLogoContainer,
    AverageRange,
    EstimatedValue,
    TotalValue,
    NextStep,
    ChevronDownButton,
    LowToHighRange,
    Notification,
  },
  beforeRouteLeave: helperFunctions.ignoreRoutes(['personal-info-form', 'verification-code', 'personal-info']),
  data() {
    return {};
  },
  async mounted() {
    this.$store.commit('SET_DHS_STATE', ['trade', 'complete'], { root: true });
    await this.updateLead();

    if (this.isAcquire) {
      try {
        await this.$store.dispatch('lead/updateMetadata');
        await this.$store.dispatch('lead/completeLead');
      } catch (err) {
        log('Error completing lead after completing acquire flow', err);
        this.$router.push({ path: `${this.getProductBasePath()}/error` });
      }
    }
  },
  computed: {
    ...mapGetters(['trade/getVehicleName', 'business/getSubscribedProducts', 'getCurrentProduct']),
    ...mapGetters('business', ['isKijijiDealer', 'getTradeInSource']),
    vehicleDetails() {
      return this['trade/getVehicleName'];
    },
    productReport() {
      switch (this.getCurrentProduct) {
        case AVRProducts.ACQUIRE:
          return this.$store.state.trade.acquire;
        default:
          return this.$store.state.trade.tradein;
      }
    },
    tradeInSourceIsTradalgo() {
      return this.$store.getters['business/getTradeInSource'].includes('tradalgo');
    },
    lowPrice() {
      return this.productReport.trade_in_value.low_value > 0
        ? inDollars(this.productReport.trade_in_value.low_value)
        : 0;
    },
    highPrice() {
      return this.productReport.trade_in_value.high_value > 0
        ? inDollars(this.productReport.trade_in_value.high_value)
        : 0;
    },
    lowListPrice() {
      return this.productReport.low_list_price > 0 ? inDollars(this.productReport.low_list_price) : 0;
    },
    highListPrice() {
      return this.productReport.high_list_price > 0 ? inDollars(this.productReport.high_list_price) : 0;
    },
    averagePrice() {
      return (this.lowPrice + this.highPrice) / 2;
    },
    averageTaxSavingValue() {
      return this.averagePrice * (parseInt(this.productReport.tax_rate, 10) / 100);
    },
    subscribesToCredit() {
      return this['business/getSubscribedProducts'].indexOf('credit') >= 0;
    },
    isAcquire() {
      return this.$store.getters.getCurrentProduct === AVRProducts.ACQUIRE;
    },
    isTrade() {
      return this.$store.getters.getCurrentProduct === AVRProducts.TRADE;
    },
    averageRangeTitle() {
      return this.isAcquire ? this.$t('message.acquire.valueRange') : this.$t('message.trade.tradeInRange');
    },
    acquireAverageEstimatedCashValue() {
      const lowAmount = this.$store.state.trade.acquire.buy_value.low_value;
      const highAmount = this.$store.state.trade.acquire.buy_value.high_value;
      return (lowAmount + highAmount) / 2;
    },
    acquireEstimatedCashValueRange() {
      return {
        low: {
          amount: inDollars(this.$store.state.trade.acquire.buy_value.low_value),
          text: this.$t('message.acquire.estimatedCashValue.fairCondition'),
        },
        average: {
          amount: inDollars(this.acquireAverageEstimatedCashValue),
          text: this.$t('message.acquire.estimatedCashValue.averageCondition'),
        },
        high: {
          amount: inDollars(this.$store.state.trade.acquire.buy_value.high_value),
          text: this.$t('message.acquire.estimatedCashValue.goodCondition'),
        },
      };
    },
    hasTradeInValuation() {
      return !!(this.productReport.trade_in_value.low_value && this.productReport.trade_in_value.high_value);
    },
    disclaimer() {
      if (this.hasTradeInValuation && !this.tradeInSourceIsTradalgo) {
        if (this.tradeInSourceIsCarfax) {
          let disclaimer = this.$t('message.trade.carfaxValuationDisclaimer');
          if (this.isKijijiDealer) {
            disclaimer += this.$t('message.trade.KijijiThirdPartyDisclaimer');
          }
          return disclaimer;
        }
        if (this.isKijijiDealer) {
          return this.$t('message.trade.kijijiDisclaimer');
        }
        return this.$t('message.trade.avDisclaimer');
      }
      return '';
    },
    displayCreditCTA() {
      const showCTA = this.subscribesToCredit && this.isTrade;
      if (this.isKijijiDealer) {
        // half complete means they completed the credit flow but not the finance flow
        return showCTA && this.$store.state.dhs.products.credit.state !== this.enums.DHSState.HALF_COMPLETE;
      }
      return showCTA;
    },
    tradeInSourceIsCarfax() {
      return this.$store.getters['business/getTradeInSource'] === TradeInSource.CARFAX;
    },
  },
  methods: {
    scrollTo(refName) {
      const element = document.getElementById(refName);
      element.scrollIntoView({ behavior: 'smooth' });
    },
    async updateLead() {
      try {
        await this.$store.dispatch('lead/updateLead');
      } catch (error) {
        this.$log(`Error in ${this.$route.path} - Update Lead`, error);
      }
    },
  },
};
</script>
<style scoped lang="scss">
@import 'src/assets/scss/variables';
h2 {
  font-size: 28px;
  font-weight: bold;
  line-height: 35px;
}
.box-top {
  flex-basis: 50%;
}
.vertical {
  border-left: 1px solid #dae2ea;
  margin-right: 30px;
  margin-left: 20px;
  margin-bottom: 15px;
}
.purchase-card {
  background: $primary;
  color: rgb(255, 255, 255);
}
.disclaimer {
  font-size: 12px;
  line-height: 18px;
  color: #4b5863;
}
.no-report-to-display {
  display: grid;
  justify-content: space-between;
  column-gap: 14px;
  grid-template-rows: 1fr;
  grid-template-columns: 1fr 1fr;

  ::v-deep .card-body {
    padding: 24px 24px 8px 24px;
  }
}

.dealer-info ::v-deep {
  .card-body {
    display: flex;
    column-gap: 10px;
  }
}

@media only screen and (max-width: $breakpoint-mobile) {
  .no-report-to-display {
    row-gap: 14px;
    grid-template-rows: repeat(2, auto);
    grid-template-columns: 1fr;
  }
}
</style>
